// Distance calculation utilities using Haversine formula

/**
 * Calculate the distance between two points using the Haversine formula
 * @param {number} lat1 - Latitude of point 1 in decimal degrees
 * @param {number} lon1 - Longitude of point 1 in decimal degrees
 * @param {number} lat2 - Latitude of point 2 in decimal degrees
 * @param {number} lon2 - Longitude of point 2 in decimal degrees
 * @returns {number} Distance in meters
 */
export const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters

  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Calculate distance between a POD and an outlet
 * @param {Object} pod - POD object with latitude and longitude
 * @param {Object} outlet - Outlet object with latitude and longitude
 * @returns {number} Distance in meters
 */
export const calculatePodOutletDistance = (pod, outlet) => {
  return haversineDistance(
    pod.latitude,
    pod.longitude,
    outlet.latitude,
    outlet.longitude
  );
};

/**
 * Find all outlets within a given radius of a POD
 * @param {Object} pod - POD object with latitude and longitude
 * @param {Array} outlets - Array of outlet objects
 * @param {number} radiusMeters - Search radius in meters
 * @returns {Array} Array of outlets within radius with distance added
 */
export const findOutletsWithinRadius = (pod, outlets, radiusMeters) => {
  return outlets
    .map((outlet) => ({
      ...outlet,
      distance: calculatePodOutletDistance(pod, outlet),
    }))
    .filter((outlet) => outlet.distance <= radiusMeters)
    .sort((a, b) => a.distance - b.distance);
};

/**
 * Find the nearest outlet to a POD
 * @param {Object} pod - POD object with latitude and longitude
 * @param {Array} outlets - Array of outlet objects
 * @returns {Object|null} Nearest outlet with distance, or null if no outlets
 */
export const findNearestOutlet = (pod, outlets) => {
  if (!outlets || outlets.length === 0) return null;

  let nearest = null;
  let minDistance = Infinity;

  outlets.forEach((outlet) => {
    const distance = calculatePodOutletDistance(pod, outlet);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = { ...outlet, distance };
    }
  });

  return nearest;
};

/**
 * Calculate estimated travel time
 * @param {number} distanceMeters - Distance in meters
 * @param {string} mode - Travel mode ('walking' or 'driving')
 * @returns {number} Time in minutes
 */
export const estimateTravelTime = (distanceMeters, mode = 'driving') => {
  const speeds = {
    walking: 83.3, // 5 km/h in meters per minute
    driving: 666.7, // 40 km/h in meters per minute
  };

  const speed = speeds[mode] || speeds.driving;
  return distanceMeters / speed;
};

/**
 * Calculate coverage statistics for a POD
 * @param {Object} pod - POD object with latitude and longitude
 * @param {Array} outlets - Array of outlet objects
 * @param {Array} radii - Array of radius values in meters
 * @returns {Object} Coverage statistics
 */
export const calculateCoverageStats = (pod, outlets, radii = [500, 1000, 2000]) => {
  const stats = {
    totalOutlets: outlets.length,
    nearestOutlet: null,
    averageDistance: 0,
    coverage: {},
  };

  if (outlets.length === 0) return stats;

  const distances = outlets.map((outlet) =>
    calculatePodOutletDistance(pod, outlet)
  );

  stats.averageDistance = distances.reduce((sum, d) => sum + d, 0) / distances.length;
  stats.nearestOutlet = Math.min(...distances);

  radii.forEach((radius) => {
    stats.coverage[radius] = distances.filter((d) => d <= radius).length;
  });

  return stats;
};

/**
 * Calculate the centroid of an array of points
 * @param {Array} points - Array of objects with latitude and longitude
 * @returns {Object} Centroid with latitude and longitude
 */
export const calculateCentroid = (points) => {
  if (!points || points.length === 0) {
    return { latitude: 0, longitude: 0 };
  }

  const sum = points.reduce(
    (acc, point) => ({
      latitude: acc.latitude + point.latitude,
      longitude: acc.longitude + point.longitude,
    }),
    { latitude: 0, longitude: 0 }
  );

  return {
    latitude: sum.latitude / points.length,
    longitude: sum.longitude / points.length,
  };
};

/**
 * Calculate the bounding box for an array of points
 * @param {Array} points - Array of objects with latitude and longitude
 * @returns {Object} Bounding box with min/max lat/lon
 */
export const calculateBoundingBox = (points) => {
  if (!points || points.length === 0) {
    return null;
  }

  const lats = points.map((p) => p.latitude);
  const lons = points.map((p) => p.longitude);

  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
  };
};
